import React, { useEffect, useState } from "react";
import {
  Sliders, ReceiptText, Wallet, Headset, Users2, Landmark,
  RefreshCcw, BadgePercent, Ticket, FileText, Check, X, Plus, Trash2,
  Plane, BedDouble, Bus as BusIcon, FileBarChart, Download, Loader2,
  TrendingUp, Trophy, Percent, Palmtree, Pencil, Mail,
  MessageSquare, Search, Phone, Filter, ClipboardList,
  Image as ImageIcon, Globe, AtSign, Undo2, FileMinus, FilePlus, ToggleRight, ToggleLeft,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { formatINR } from "@/mock";
import { toast } from "sonner";
import { downloadInvoicePdf } from "@/lib/invoicePdf";
import {
  apiGetMarkup, apiSetMarkup, apiCreateDeposit, apiListDeposits, apiActionDeposit,
  apiListTravellers, apiAddTraveller, apiDelTraveller, apiListOffers, apiAddOffer,
  apiDelOffer, apiReportsSummary, apiStatement, apiCreateServiceReq, apiListServiceReq,
  apiResolveServiceReq, apiListBanks, apiAddBank, apiDelBank, apiListAmendments,
  apiAmendmentStatus, apiCreateAmendment, apiListBookings,
  apiAgentReports, apiAdminAgentReports, apiGetInvoice,
  apiAdminListHolidays, apiAdminCreateHoliday, apiAdminUpdateHoliday,
  apiAdminDeleteHoliday, apiAdminHolidayEnquiries,
  apiListEnquiries, apiEnquiryStatus,
  apiAdminListBanners, apiAdminCreateBanner, apiAdminUpdateBanner, apiAdminDeleteBanner,
  apiAdminListSeo, apiAdminSetSeo, apiAdminSubscribers,
  apiAdminListNotes, apiAdminCreateNote, apiAdminDeleteNote, apiListRefunds, apiRefundStatus,
  apiWalletTopupOrder, apiWalletTopupVerify, apiAdminWalletTopups,
} from "@/api";
import { loadRazorpay } from "@/lib/razorpay";
import { useAuth } from "@/context/AuthContext";
import { refreshSavedTravellers } from "@/components/TravellerSuggest";
import { ReportCenter } from "@/components/dashboard/ReportCenter";
import { MarkupOverview, GroupManager } from "@/components/dashboard/MarkupSettings";
import { SavedQuotes } from "@/components/SavedQuotes";

const Card = ({ children, className = "", ...rest }) => (
  <div {...rest} className={`rounded-2xl border border-slate-100 bg-white p-5 shadow-sm ${className}`}>{children}</div>
);
const Field = (props) => (
  <input {...props} className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-[#1B1F5B]" />
);
const Btn = ({ children, className = "", ...p }) => (
  <button {...p} className={`rounded-lg bg-[#1B1F5B] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#141844] disabled:opacity-60 ${className}`}>{children}</button>
);

// ---------------- MARKUP ----------------
const MarkupPanel = () => {
  const [m, setM] = useState({ flight: 0, hotel: 0, bus: 0, flightType: "flat", hotelType: "flat" });
  useEffect(() => { apiGetMarkup().then((d) => setM((p) => ({ ...p, ...d }))).catch(() => {}); }, []);
  const save = async () => {
    try { await apiSetMarkup(m); toast.success("Markup saved"); } catch { toast.error("Could not save markup"); }
  };
  return (
    <Card>
      <h3 className="text-base font-extrabold text-slate-800">Manage Markup</h3>
      <p className="mb-4 text-xs text-slate-400">Apna commission / markup set karein — per booking add hoga.</p>
      <div className="grid gap-4 sm:grid-cols-3">
        {["flight", "hotel", "bus"].map((k) => (
          <div key={k}>
            <label className="text-xs font-bold uppercase text-slate-500">{k}</label>
            <div className="mt-1 flex gap-1">
              <Field type="number" data-testid={`markup-${k}`} value={m[k]} onChange={(e) => setM({ ...m, [k]: parseFloat(e.target.value) || 0 })} />
              {k !== "bus" && (
                <select value={m[`${k}Type`]} onChange={(e) => setM({ ...m, [`${k}Type`]: e.target.value })}
                  className="rounded-lg border border-slate-200 px-2 text-xs font-bold text-slate-600">
                  <option value="flat">₹</option><option value="percent">%</option>
                </select>
              )}
            </div>
          </div>
        ))}
      </div>
      <Btn data-testid="markup-save-btn" className="mt-4" onClick={save}>Save Markup</Btn>
    </Card>
  );
};

// ---------------- STATEMENT ----------------
const StatementPanel = () => {
  const [d, setD] = useState({ transactions: [], totalCredit: 0, totalDebit: 0, balance: 0 });
  useEffect(() => { apiStatement().then(setD).catch(() => {}); }, []);
  return (
    <Card>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-base font-extrabold text-slate-800">Statement / Ledger</h3>
        <div className="flex gap-2 text-xs font-bold">
          <span className="rounded-lg bg-emerald-50 px-3 py-1 text-emerald-600">Credit {formatINR(d.totalCredit)}</span>
          <span className="rounded-lg bg-red-50 px-3 py-1 text-red-500">Debit {formatINR(d.totalDebit)}</span>
          <span className="rounded-lg bg-[#1B1F5B]/5 px-3 py-1 text-[#1B1F5B]">Balance {formatINR(d.balance)}</span>
        </div>
      </div>
      <div className="mt-3 space-y-2" data-testid="statement-list">
        {d.transactions.length === 0 && <p className="py-6 text-center text-sm text-slate-400">No transactions yet</p>}
        {d.transactions.map((t) => (
          <div key={t.txn_id} className="flex items-center justify-between rounded-xl border border-slate-100 px-4 py-2.5 text-sm">
            <span className="text-slate-600">{t.note}<span className="ml-2 text-[10px] text-slate-300">{(t.created_at || "").slice(0, 10)}</span></span>
            <span className={`font-bold ${t.type === "credit" ? "text-emerald-600" : "text-red-500"}`}>
              {t.type === "credit" ? "+" : "-"}{formatINR(t.amount)}
            </span>
          </div>
        ))}
      </div>
    </Card>
  );
};

// ---------------- TOPUP / ADD MONEY ----------------
const TopupPanel = () => {
  const { user, refreshMe } = useAuth();
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("upi");
  const [reference, setRef] = useState("");
  const [list, setList] = useState([]);
  const [banks, setBanks] = useState([]);
  const [payAmount, setPayAmount] = useState("");
  const [paying, setPaying] = useState(false);
  const load = () => { apiListDeposits().then((d) => setList(d.requests || [])).catch(() => {}); };
  useEffect(() => { load(); apiListBanks().then((d) => setBanks(d.banks || [])).catch(() => {}); }, []);
  const submit = async () => {
    const amt = parseInt(amount, 10);
    if (!amt || amt <= 0) return toast.error("Enter valid amount");
    try { await apiCreateDeposit({ amount: amt, method, reference }); toast.success("Top-up request sent for approval"); setAmount(""); setRef(""); load(); }
    catch { toast.error("Could not send request"); }
  };
  const quick = [1000, 5000, 10000, 25000];
  const payNow = async () => {
    const amt = parseInt(payAmount, 10);
    if (!amt || amt < 100) return toast.error("Minimum top-up is Rs.100");
    setPaying(true);
    try {
      const ok = await loadRazorpay();
      if (!ok) { toast.error("Could not load payment gateway. Try again."); setPaying(false); return; }
      const order = await apiWalletTopupOrder(amt);
      const rzp = new window.Razorpay({
        key: order.key_id, amount: order.amount, currency: order.currency, order_id: order.order_id,
        name: "FLYTRIP", description: "Wallet top-up",
        prefill: { name: user?.name || "", email: user?.email || "", contact: user?.phone || "" },
        theme: { color: "#1B1F5B" },
        handler: async (resp) => {
          try {
            const res = await apiWalletTopupVerify({
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success(`Wallet credited ${formatINR(res.amount)}! New balance ${formatINR(res.balance)}`);
            setPayAmount("");
            await refreshMe();
          } catch {
            toast.error("Payment verification failed. If money was deducted, contact support.");
          } finally { setPaying(false); }
        },
        modal: { ondismiss: () => { toast.info("Payment cancelled"); setPaying(false); } },
      });
      rzp.on("payment.failed", (r) => { toast.error(r?.error?.description || "Payment failed"); setPaying(false); });
      rzp.open();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start payment");
      setPaying(false);
    }
  };
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card data-testid="wallet-instant-topup" className="border-[#1B1F5B]/15 bg-gradient-to-br from-[#1B1F5B]/[0.04] to-white">
        <div className="mb-1 flex items-center gap-2">
          <Wallet className="h-5 w-5 text-[#1B1F5B]" />
          <h3 className="text-base font-extrabold text-slate-800">Add Money to Wallet</h3>
        </div>
        <p className="mb-3 text-xs text-slate-400">Razorpay se instant pay karein — wallet turant load ho jayega.</p>
        <div className="mb-3 flex flex-wrap gap-2">
          {quick.map((q) => (
            <button key={q} type="button" data-testid={`wallet-quick-${q}`} onClick={() => setPayAmount(String(q))}
              className={`rounded-lg border px-3 py-1.5 text-xs font-bold transition-colors ${String(q) === payAmount ? "border-[#1B1F5B] bg-[#1B1F5B] text-white" : "border-slate-200 text-slate-600 hover:border-[#1B1F5B]"}`}>
              {formatINR(q)}
            </button>
          ))}
        </div>
        <div className="space-y-2.5">
          <Field type="number" data-testid="wallet-pay-amount" placeholder="Enter amount (min Rs.100)" value={payAmount} onChange={(e) => setPayAmount(e.target.value)} />
          <Btn data-testid="wallet-pay-btn" className="w-full" onClick={payNow} disabled={paying}>
            {paying ? "Processing..." : "Pay & Load Wallet"}
          </Btn>
        </div>
      </Card>
      <Card>
        <h3 className="text-base font-extrabold text-slate-800">Add Money (Manual Top-up Request)</h3>
        <p className="mb-3 text-xs text-slate-400">Bank/UPI se paisa transfer karke request bhejein — admin approve karega.</p>
        <div className="space-y-2.5">
          <Field type="number" data-testid="topup-amount" placeholder="Amount (INR)" value={amount} onChange={(e) => setAmount(e.target.value)} />
          <select value={method} onChange={(e) => setMethod(e.target.value)} className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm">
            <option value="upi">UPI</option><option value="bank_transfer">Bank Transfer</option><option value="cheque">Cheque</option>
          </select>
          <Field data-testid="topup-ref" placeholder="Reference / UTR no." value={reference} onChange={(e) => setRef(e.target.value)} />
          <Btn data-testid="topup-submit-btn" className="w-full" onClick={submit}>Send Top-up Request</Btn>
        </div>
        {banks.length > 0 && (
          <div className="mt-4 rounded-xl bg-slate-50 p-3 text-xs">
            <p className="font-bold text-slate-600">Deposit to:</p>
            {banks.map((b) => (
              <p key={b.bank_id} className="mt-1 text-slate-500">{b.bankName} - A/C {b.accountNumber} ({b.ifsc})</p>
            ))}
          </div>
        )}
      </Card>
      <Card className="lg:col-span-2">
        <h3 className="text-base font-extrabold text-slate-800">My Top-up Requests</h3>
        <div className="mt-3 space-y-2" data-testid="topup-list">
          {list.length === 0 && <p className="py-6 text-center text-sm text-slate-400">No requests yet</p>}
          {list.map((r) => (
            <div key={r.request_id} className="flex items-center justify-between rounded-xl border border-slate-100 px-4 py-2.5 text-sm">
              <span className="text-slate-600">{formatINR(r.amount)} <span className="text-[10px] uppercase text-slate-400">{r.method}</span></span>
              <span className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${r.status === "approved" ? "bg-emerald-50 text-emerald-600" : r.status === "rejected" ? "bg-red-50 text-red-500" : "bg-amber-50 text-amber-600"}`}>{r.status}</span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};

// ---------------- SERVICE REQUESTS ----------------
const ServicePanel = ({ admin }) => {
  const [subject, setSubject] = useState("");
  const [category, setCategory] = useState("general");
  const [message, setMessage] = useState("");
  const [list, setList] = useState([]);
  const load = () => apiListServiceReq().then((d) => setList(d.tickets || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const submit = async () => {
    if (!subject) return toast.error("Enter subject");
    try { await apiCreateServiceReq({ subject, category, message }); toast.success("Service request raised"); setSubject(""); setMessage(""); load(); }
    catch { toast.error("Failed"); }
  };
  const resolve = async (id) => { await apiResolveServiceReq(id); toast.success("Marked resolved"); load(); };
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      {!admin && (
        <Card>
          <h3 className="text-base font-extrabold text-slate-800">Raise Service Request</h3>
          <div className="mt-3 space-y-2.5">
            <Field data-testid="svc-subject" placeholder="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} />
            <select value={category} onChange={(e) => setCategory(e.target.value)} className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm">
              <option value="general">General</option><option value="booking">Booking</option><option value="wallet">Wallet</option><option value="refund">Refund</option>
            </select>
            <textarea data-testid="svc-message" placeholder="Describe your issue" value={message} onChange={(e) => setMessage(e.target.value)}
              className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-[#1B1F5B]" rows={3} />
            <Btn data-testid="svc-submit-btn" className="w-full" onClick={submit}>Submit Request</Btn>
          </div>
        </Card>
      )}
      <Card className={admin ? "lg:col-span-2" : ""}>
        <h3 className="text-base font-extrabold text-slate-800">{admin ? "All Service Requests" : "My Requests"}</h3>
        <div className="mt-3 space-y-2" data-testid="svc-list">
          {list.length === 0 && <p className="py-6 text-center text-sm text-slate-400">No requests</p>}
          {list.map((t) => (
            <div key={t.ticket_id} className="flex items-center justify-between rounded-xl border border-slate-100 px-4 py-2.5 text-sm">
              <div>
                <p className="font-bold text-slate-700">{t.subject} <span className="ml-1 rounded bg-slate-100 px-1.5 text-[10px] uppercase text-slate-500">{t.category}</span></p>
                {admin && <p className="text-xs text-slate-400">{t.user_name} - {t.user_email}</p>}
              </div>
              <div className="flex items-center gap-2">
                <span className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${t.status === "resolved" ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600"}`}>{t.status}</span>
                {admin && t.status !== "resolved" && <button onClick={() => resolve(t.ticket_id)} className="rounded-lg bg-emerald-500 px-2 py-1 text-[11px] font-bold text-white">Resolve</button>}
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};

// ---------------- MY CUSTOMERS / TRAVELLERS ----------------
const TravellersPanel = () => {
  const [list, setList] = useState([]);
  const blankT = { title: "Mr", firstName: "", lastName: "", type: "ADULT", dob: "", email: "", phone: "", passport: "", passportExpiry: "", nationality: "IN", pan: "" };
  const [f, setF] = useState(blankT);
  const load = () => apiListTravellers().then((d) => setList(d.travellers || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!f.firstName || !f.lastName) return toast.error("Enter name");
    try { await apiAddTraveller(f); toast.success("Customer saved — it will auto-suggest at checkout"); setF(blankT); refreshSavedTravellers(); load(); } catch { toast.error("Failed"); }
  };
  const del = async (id) => { await apiDelTraveller(id); load(); };
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card>
        <h3 className="text-base font-extrabold text-slate-800">Add Traveller / Customer</h3>
        <div className="mt-3 grid grid-cols-2 gap-2">
          <select value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="rounded-lg border border-slate-200 px-2 py-2.5 text-sm">
            <option>Mr</option><option>Mrs</option><option>Ms</option><option>Mstr</option>
          </select>
          <select value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} className="rounded-lg border border-slate-200 px-2 py-2.5 text-sm">
            <option value="ADULT">Adult</option><option value="CHILD">Child</option><option value="INFANT">Infant</option>
          </select>
          <Field data-testid="trv-first" placeholder="First name" value={f.firstName} onChange={(e) => setF({ ...f, firstName: e.target.value })} />
          <Field data-testid="trv-last" placeholder="Last name" value={f.lastName} onChange={(e) => setF({ ...f, lastName: e.target.value })} />
          <Field data-testid="trv-email" type="email" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
          <Field data-testid="trv-phone" placeholder="Mobile" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} />
          <label className="text-[11px] font-semibold text-slate-500">Date of birth<Field data-testid="trv-dob" type="date" value={f.dob} onChange={(e) => setF({ ...f, dob: e.target.value })} /></label>
          <label className="text-[11px] font-semibold text-slate-500">PAN<Field data-testid="trv-pan" placeholder="ABCDE1234F" maxLength={10} value={f.pan} onChange={(e) => setF({ ...f, pan: e.target.value.toUpperCase() })} /></label>
          <Field data-testid="trv-passport" placeholder="Passport no." value={f.passport} onChange={(e) => setF({ ...f, passport: e.target.value.toUpperCase() })} />
          <label className="text-[11px] font-semibold text-slate-500">Passport expiry<Field data-testid="trv-pexp" type="date" value={f.passportExpiry} onChange={(e) => setF({ ...f, passportExpiry: e.target.value })} /></label>
        </div>
        <Btn data-testid="trv-add-btn" className="mt-3 w-full" onClick={add}>Save Traveller</Btn>
      </Card>
      <Card>
        <h3 className="text-base font-extrabold text-slate-800">Saved Travellers</h3>
        <div className="mt-3 space-y-2" data-testid="trv-list">
          {list.length === 0 && <p className="py-6 text-center text-sm text-slate-400">No saved travellers</p>}
          {list.map((t) => (
            <div key={t.traveller_id} className="flex items-center justify-between rounded-xl border border-slate-100 px-4 py-2.5 text-sm">
              <span className="text-slate-600">{t.title} {t.firstName} {t.lastName} <span className="text-[10px] uppercase text-slate-400">{t.type}</span>
                {(t.phone || t.email) && <span className="block text-[11px] text-slate-400">{[t.phone, t.email, t.passport && `PP ${t.passport}`].filter(Boolean).join(" · ")}</span>}</span>
              <button onClick={() => del(t.traveller_id)} className="text-red-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};

// ---------------- ADMIN: DEPOSIT APPROVALS ----------------
const DepositApprovals = () => {
  const [list, setList] = useState([]);
  const load = () => apiListDeposits().then((d) => setList(d.requests || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const act = async (id, ok) => { await apiActionDeposit(id, ok); toast.success(ok ? "Approved & wallet credited" : "Rejected"); load(); };
  return (
    <Card>
      <h3 className="text-base font-extrabold text-slate-800">Agent Top-up Approvals</h3>
      <div className="mt-3 space-y-2" data-testid="deposit-approvals">
        {list.length === 0 && <p className="py-6 text-center text-sm text-slate-400">No top-up requests</p>}
        {list.map((r) => (
          <div key={r.request_id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-slate-100 px-4 py-2.5 text-sm">
            <div>
              <p className="font-bold text-slate-700">{formatINR(r.amount)} <span className="text-[10px] uppercase text-slate-400">{r.method}</span></p>
              <p className="text-xs text-slate-400">{r.agent_name} - {r.reference || "no ref"}</p>
            </div>
            {r.status === "pending" ? (
              <div className="flex gap-2">
                <button data-testid={`deposit-approve-${r.request_id}`} onClick={() => act(r.request_id, true)} className="flex items-center gap-1 rounded-lg bg-emerald-500 px-2.5 py-1.5 text-[11px] font-bold text-white"><Check className="h-3 w-3" />Approve</button>
                <button onClick={() => act(r.request_id, false)} className="flex items-center gap-1 rounded-lg bg-red-500 px-2.5 py-1.5 text-[11px] font-bold text-white"><X className="h-3 w-3" />Reject</button>
              </div>
            ) : (
              <span className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${r.status === "approved" ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-500"}`}>{r.status}</span>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
};

// ---------------- ADMIN: WALLET LOADS (RAZORPAY) REPORT ----------------
const WalletLoadsPanel = () => {
  const [data, setData] = useState({ topups: [], total_amount: 0, count: 0 });
  useEffect(() => { apiAdminWalletTopups().then(setData).catch(() => {}); }, []);
  const exportCsv = () => {
    const rows = [["Date", "Agent", "Email", "Agency", "Amount", "Payment ID", "Order ID"]];
    data.topups.forEach((t) => rows.push([
      new Date(t.created_at).toLocaleString(), t.agent_name, t.agent_email, t.agency || "",
      t.amount, t.payment_id, t.order_id,
    ]));
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = "wallet-loads.csv"; a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <Card>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-base font-extrabold text-slate-800">Wallet Loads (Razorpay)</h3>
          <p className="text-xs text-slate-400">Agents ne PG se jo wallet load kiya uski report</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="rounded-xl bg-[#1B1F5B]/5 px-3 py-1.5 text-right">
            <p className="text-[10px] font-bold uppercase text-slate-400">Total Loaded</p>
            <p data-testid="wallet-loads-total" className="text-sm font-extrabold text-[#1B1F5B]">{formatINR(data.total_amount)}</p>
          </div>
          {data.topups.length > 0 && <Btn data-testid="wallet-loads-export" onClick={exportCsv}>Export CSV</Btn>}
        </div>
      </div>
      <div className="overflow-x-auto" data-testid="wallet-loads-table">
        {data.topups.length === 0 ? (
          <p className="py-8 text-center text-sm text-slate-400">No wallet loads yet</p>
        ) : (
          <table className="w-full text-left text-sm">
            <thead className="text-xs uppercase text-slate-400">
              <tr className="border-b">
                <th className="pb-2 pr-3">Date</th><th className="pb-2 pr-3">Agent</th>
                <th className="pb-2 pr-3">Agency</th><th className="pb-2 pr-3 text-right">Amount</th>
                <th className="pb-2">Payment ID</th>
              </tr>
            </thead>
            <tbody>
              {data.topups.map((t) => (
                <tr key={t.topup_id} className="border-b border-slate-50">
                  <td className="py-2.5 pr-3 text-slate-500">{new Date(t.created_at).toLocaleDateString()}</td>
                  <td className="py-2.5 pr-3"><span className="font-bold text-slate-700">{t.agent_name}</span><br /><span className="text-[11px] text-slate-400">{t.agent_email}</span></td>
                  <td className="py-2.5 pr-3 text-slate-500">{t.agency || "-"}</td>
                  <td className="py-2.5 pr-3 text-right font-extrabold text-emerald-600">{formatINR(t.amount)}</td>
                  <td className="py-2.5 font-mono text-[11px] text-slate-400">{t.payment_id}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Card>
  );
};


// ---------------- ADMIN: OFFERS ----------------
const OffersPanel = () => {
  const [list, setList] = useState([]);
  const [f, setF] = useState({ title: "", code: "", discount: "", product: "flight" });
  const load = () => apiListOffers().then((d) => setList(d.offers || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const add = async () => { if (!f.title) return toast.error("Enter title"); await apiAddOffer(f); toast.success("Offer added"); setF({ title: "", code: "", discount: "", product: "flight" }); load(); };
  const del = async (id) => { await apiDelOffer(id); load(); };
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card>
        <h3 className="text-base font-extrabold text-slate-800">Create Offer / Coupon</h3>
        <div className="mt-3 space-y-2.5">
          <Field data-testid="offer-title" placeholder="Title (e.g. Flat 500 off)" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
          <div className="grid grid-cols-2 gap-2">
            <Field data-testid="offer-code" placeholder="Code FLY500" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} />
            <Field placeholder="Discount" value={f.discount} onChange={(e) => setF({ ...f, discount: e.target.value })} />
          </div>
          <select value={f.product} onChange={(e) => setF({ ...f, product: e.target.value })} className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm">
            <option value="flight">Flight</option><option value="hotel">Hotel</option><option value="bus">Bus</option>
          </select>
          <Btn data-testid="offer-add-btn" className="w-full" onClick={add}>Add Offer</Btn>
        </div>
      </Card>
      <Card>
        <h3 className="text-base font-extrabold text-slate-800">Active Offers</h3>
        <div className="mt-3 space-y-2" data-testid="offers-list">
          {list.length === 0 && <p className="py-6 text-center text-sm text-slate-400">No offers</p>}
          {list.map((o) => (
            <div key={o.offer_id} className="flex items-center justify-between rounded-xl border border-slate-100 px-4 py-2.5 text-sm">
              <span className="text-slate-600"><b>{o.title}</b> {o.code && <span className="ml-1 rounded bg-[#F5B32B]/20 px-1.5 text-[10px] font-bold text-[#B57A00]">{o.code}</span>} <span className="text-[10px] uppercase text-slate-400">{o.product}</span></span>
              <button onClick={() => del(o.offer_id)} className="text-red-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};

// ---------------- ADMIN: BANK DETAILS ----------------
const BanksPanel = () => {
  const [list, setList] = useState([]);
  const [f, setF] = useState({ bankName: "", accountName: "", accountNumber: "", ifsc: "", branch: "" });
  const load = () => apiListBanks().then((d) => setList(d.banks || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const add = async () => { if (!f.bankName || !f.accountNumber) return toast.error("Fill bank & a/c"); await apiAddBank(f); toast.success("Bank added"); setF({ bankName: "", accountName: "", accountNumber: "", ifsc: "", branch: "" }); load(); };
  const del = async (id) => { await apiDelBank(id); load(); };
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card>
        <h3 className="text-base font-extrabold text-slate-800">Add Bank Account</h3>
        <div className="mt-3 space-y-2.5">
          <Field data-testid="bank-name" placeholder="Bank name" value={f.bankName} onChange={(e) => setF({ ...f, bankName: e.target.value })} />
          <Field placeholder="Account holder" value={f.accountName} onChange={(e) => setF({ ...f, accountName: e.target.value })} />
          <div className="grid grid-cols-2 gap-2">
            <Field data-testid="bank-acc" placeholder="Account no." value={f.accountNumber} onChange={(e) => setF({ ...f, accountNumber: e.target.value })} />
            <Field placeholder="IFSC" value={f.ifsc} onChange={(e) => setF({ ...f, ifsc: e.target.value })} />
          </div>
          <Btn data-testid="bank-add-btn" className="w-full" onClick={add}>Add Bank</Btn>
        </div>
      </Card>
      <Card>
        <h3 className="text-base font-extrabold text-slate-800">Bank Accounts</h3>
        <div className="mt-3 space-y-2" data-testid="banks-list">
          {list.length === 0 && <p className="py-6 text-center text-sm text-slate-400">No banks</p>}
          {list.map((b) => (
            <div key={b.bank_id} className="flex items-center justify-between rounded-xl border border-slate-100 px-4 py-2.5 text-sm">
              <span className="text-slate-600"><b>{b.bankName}</b> - {b.accountNumber} <span className="text-[10px] text-slate-400">{b.ifsc}</span></span>
              <button onClick={() => del(b.bank_id)} className="text-red-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};

// ---------------- AMENDMENTS (cancel/reschedule queue) ----------------
const AmendmentsPanel = ({ admin }) => {
  const [list, setList] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [f, setF] = useState({ bookingId: "", type: "cancel", reason: "" });
  const load = () => apiListAmendments().then((d) => setList(d.amendments || [])).catch(() => {});
  useEffect(() => {
    load();
    if (!admin) apiListBookings().then((d) => setBookings(d.bookings || [])).catch(() => {});
  }, [admin]);
  const setStatus = async (id, s) => { await apiAmendmentStatus(id, s); toast.success("Updated"); load(); };
  const submit = async () => {
    if (!f.bookingId) return toast.error("Please select a booking");
    if (!f.reason.trim()) return toast.error("Please enter a reason");
    try {
      await apiCreateAmendment(f);
      toast.success("Amendment request submitted");
      setF({ bookingId: "", type: "cancel", reason: "" });
      load();
    } catch { toast.error("Could not submit request"); }
  };
  return (
    <div className={admin ? "" : "grid gap-4 lg:grid-cols-[340px_1fr]"}>
      {!admin && (
        <Card>
          <h3 className="text-base font-extrabold text-slate-800">Raise a Request</h3>
          <p className="mb-3 text-xs text-slate-400">Cancel or reschedule an existing booking — our team will process it.</p>
          <div className="space-y-2.5">
            <select data-testid="amd-booking" value={f.bookingId} onChange={(e) => setF({ ...f, bookingId: e.target.value })}
              className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-[#0E7C6B]">
              <option value="">Select a booking…</option>
              {bookings.map((b) => (
                <option key={b.booking_id} value={b.booking_id}>{b.pnr} — {b.route}</option>
              ))}
            </select>
            <select data-testid="amd-type" value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })}
              className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-[#0E7C6B]">
              <option value="cancel">Cancel Booking</option>
              <option value="reschedule">Reschedule / Date Change</option>
            </select>
            <textarea data-testid="amd-reason" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} rows={3}
              placeholder="Reason / details" className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-[#0E7C6B]" />
            <Btn data-testid="amd-submit-btn" onClick={submit}>Submit Request</Btn>
            {bookings.length === 0 && <p className="text-center text-xs text-slate-400">No bookings yet to amend.</p>}
          </div>
        </Card>
      )}
      <Card>
        <h3 className="text-base font-extrabold text-slate-800">{admin ? "Amendment Tracking (Cancel / Reschedule)" : "My Amendment Queue"}</h3>
        <div className="mt-3 space-y-2" data-testid="amendments-list">
          {list.length === 0 && <p className="py-6 text-center text-sm text-slate-400">No amendment requests</p>}
          {list.map((a) => (
            <div key={a.amendment_id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-slate-100 px-4 py-2.5 text-sm">
              <div>
                <p className="font-bold text-slate-700">{a.pnr} <span className="ml-1 rounded bg-slate-100 px-1.5 text-[10px] uppercase text-slate-500">{a.type}</span></p>
                <p className="text-xs text-slate-400">{a.route} — {a.reason}</p>
              </div>
              {admin && a.status === "requested" ? (
                <div className="flex gap-2">
                  <button onClick={() => setStatus(a.amendment_id, "processed")} className="rounded-lg bg-emerald-500 px-2.5 py-1.5 text-[11px] font-bold text-white">Process</button>
                  <button onClick={() => setStatus(a.amendment_id, "rejected")} className="rounded-lg bg-red-500 px-2.5 py-1.5 text-[11px] font-bold text-white">Reject</button>
                </div>
              ) : (
                <span className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${a.status === "processed" ? "bg-emerald-50 text-emerald-600" : a.status === "rejected" ? "bg-red-50 text-red-500" : "bg-amber-50 text-amber-600"}`}>{a.status}</span>
              )}
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};

// ---------------- REPORTS (Flight / Hotel / Bus booking reports) ----------------
const REPORT_TYPES = [
  { id: "flight", label: "Flights", icon: Plane, chip: "bg-sky-50 text-sky-600", bar: "bg-sky-500" },
  { id: "hotel", label: "Hotels", icon: BedDouble, chip: "bg-emerald-50 text-emerald-600", bar: "bg-emerald-500" },
  { id: "bus", label: "Bus", icon: BusIcon, chip: "bg-amber-50 text-amber-600", bar: "bg-amber-500" },
];

const ReportsPanel = () => {
  const [data, setData] = useState({ bookings: [], monthly: [], byType: {}, totalCommission: 0 });
  const [loading, setLoading] = useState(true);
  const [typeF, setTypeF] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [pdfBusy, setPdfBusy] = useState("");
  useEffect(() => {
    apiAgentReports().then(setData).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const bdate = (b) => (b.created_at || b.date || "").slice(0, 10);
  const filtered = data.bookings.filter((b) =>
    (typeF === "all" || (b.type || "flight") === typeF) && (!from || bdate(b) >= from) && (!to || bdate(b) <= to));
  const totalSales = filtered.reduce((a, b) => a + (b.total || 0), 0);
  const totalComm = filtered.reduce((a, b) => a + (b.commission || 0), 0);
  const monthName = (m) => {
    const [y, mm] = (m || "").split("-");
    return mm ? new Date(y, mm - 1).toLocaleString("en", { month: "short" }) + " " + y.slice(2) : m;
  };
  const chartData = (data.monthly || []).map((m) => ({ ...m, label: monthName(m.month) }));

  const downloadPdf = async (b) => {
    setPdfBusy(b.booking_id);
    try {
      const inv = await apiGetInvoice(b.booking_id);
      downloadInvoicePdf(inv);
      toast.success("Invoice downloaded");
    } catch { toast.error("Could not generate invoice"); }
    finally { setPdfBusy(""); }
  };

  const exportCsv = () => {
    const rows = [["PNR", "Type", "Route", "Travel Date", "Pax", "Amount", "Commission", "Status", "Booked On"]];
    filtered.forEach((b) => rows.push([b.pnr, b.type, b.route, b.date || "", b.pax ?? "", b.total ?? 0, b.commission ?? 0, b.status, bdate(b)]));
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = `flytrip-booking-report-${from || "all"}-${to || "all"}.csv`;
    a.click();
    toast.success("Report downloaded");
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-[#0E7C6B]" /></div>;

  return (
    <div className="space-y-4" data-testid="reports-panel">
      {/* Filters */}
      <Card>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="mb-1 block text-[10px] font-bold uppercase text-slate-400">Booking Type</label>
            <select data-testid="reports-type-filter" value={typeF} onChange={(e) => setTypeF(e.target.value)}
              className="rounded-xl border border-slate-200 px-3 py-2.5 text-sm font-semibold outline-none focus:border-[#0E7C6B]">
              <option value="all">All (Flight + Hotel + Bus)</option>
              <option value="flight">Flights</option>
              <option value="hotel">Hotels</option>
              <option value="bus">Bus</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-[10px] font-bold uppercase text-slate-400">From</label>
            <input data-testid="reports-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)}
              className="rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-[#0E7C6B]" />
          </div>
          <div>
            <label className="mb-1 block text-[10px] font-bold uppercase text-slate-400">To</label>
            <input data-testid="reports-to" type="date" value={to} onChange={(e) => setTo(e.target.value)}
              className="rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-[#0E7C6B]" />
          </div>
          <button data-testid="reports-reset" onClick={() => { setTypeF("all"); setFrom(""); setTo(""); }}
            className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-bold text-slate-500 hover:bg-slate-50">Reset</button>
          <button data-testid="reports-export" onClick={exportCsv} disabled={!filtered.length}
            className="ml-auto flex items-center gap-1.5 rounded-xl bg-[#0E7C6B] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#0A5348] disabled:opacity-50">
            <Download className="h-4 w-4" /> Export CSV
          </button>
        </div>
      </Card>

      {/* KPI summary */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Card className="bg-gradient-to-br from-[#1B1F5B] to-[#252A75] !border-0">
          <p className="text-[10px] font-bold uppercase tracking-widest text-white/50">Total Sales</p>
          <p className="mt-1 text-2xl font-extrabold text-white" data-testid="reports-total-sales">{formatINR(totalSales)}</p>
          <p className="mt-0.5 text-xs font-semibold text-white/60">{filtered.length} bookings</p>
        </Card>
        <Card className="bg-gradient-to-br from-[#0E7C6B] to-[#12A08A] !border-0" data-testid="reports-total-commission">
          <div className="flex items-center gap-1.5 text-white/60"><Percent className="h-3.5 w-3.5" /><p className="text-[10px] font-bold uppercase tracking-widest">Commission Earned</p></div>
          <p className="mt-1 text-2xl font-extrabold text-white">{formatINR(totalComm)}</p>
          <p className="mt-0.5 text-xs font-semibold text-white/60">from your markup</p>
        </Card>
        {[
          { id: "flight", label: "Flights", icon: Plane, chip: "bg-sky-50 text-sky-600", bar: "bg-sky-500" },
          { id: "hotel", label: "Hotels", icon: BedDouble, chip: "bg-emerald-50 text-emerald-600", bar: "bg-emerald-500" },
        ].map((t) => {
          const arr = filtered.filter((b) => (b.type || "flight") === t.id);
          const amt = arr.reduce((a, b) => a + (b.total || 0), 0);
          const share = totalSales ? Math.round((amt / totalSales) * 100) : 0;
          const Icon = t.icon;
          return (
            <Card key={t.id} data-testid={`reports-kpi-${t.id}`}>
              <div className="flex items-center justify-between">
                <span className={`flex h-9 w-9 items-center justify-center rounded-xl ${t.chip}`}><Icon className="h-4 w-4" /></span>
                <span className="text-xs font-bold text-slate-400">{arr.length}</span>
              </div>
              <p className="mt-2 text-lg font-extrabold text-slate-800">{formatINR(amt)}</p>
              <p className="text-[11px] font-semibold uppercase text-slate-400">{t.label}</p>
              <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-100"><div className={`h-full rounded-full ${t.bar}`} style={{ width: `${share}%` }} /></div>
            </Card>
          );
        })}
      </div>

      {/* Monthly trend chart */}
      <Card>
        <div className="mb-2 flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-[#0E7C6B]" />
          <h3 className="text-base font-extrabold text-slate-800">Monthly Sales Trend</h3>
        </div>
        {chartData.length === 0 ? (
          <p className="py-10 text-center text-sm text-slate-400" data-testid="reports-chart-empty">No sales data yet to chart.</p>
        ) : (
          <div className="h-64 w-full" data-testid="reports-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} tickFormatter={(v) => v >= 1000 ? `${v / 1000}k` : v} />
                <Tooltip cursor={{ fill: "rgba(14,124,107,0.06)" }} formatter={(v, n) => [formatINR(v), n === "sales" ? "Sales" : "Commission"]}
                  contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />
                <Bar dataKey="sales" radius={[6, 6, 0, 0]} maxBarSize={44}>
                  {chartData.map((_, i) => <Cell key={i} fill="#1B1F5B" />)}
                </Bar>
                <Bar dataKey="commission" radius={[6, 6, 0, 0]} maxBarSize={44}>
                  {chartData.map((_, i) => <Cell key={i} fill="#0E7C6B" />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      {/* Bookings table */}
      <Card>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-base font-extrabold text-slate-800">Booking Report</h3>
          <span className="text-xs font-semibold text-slate-400">{filtered.length} records</span>
        </div>
        {filtered.length === 0 ? (
          <p className="py-10 text-center text-sm text-slate-400" data-testid="reports-empty">No bookings found for the selected filters.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-sm" data-testid="reports-table">
              <thead>
                <tr className="border-b border-slate-100 text-left text-[10px] font-bold uppercase tracking-wide text-slate-400">
                  <th className="pb-2 pr-3">PNR</th><th className="pb-2 pr-3">Type</th><th className="pb-2 pr-3">Route / Hotel</th>
                  <th className="pb-2 pr-3">Date</th><th className="pb-2 pr-3">Pax</th><th className="pb-2 pr-3 text-right">Amount</th>
                  <th className="pb-2 pr-3 text-right">Commission</th><th className="pb-2 pr-3 text-right">Status</th><th className="pb-2 text-right">Invoice</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((b) => {
                  const meta = { flight: { c: "bg-sky-50 text-sky-600", I: Plane }, hotel: { c: "bg-emerald-50 text-emerald-600", I: BedDouble }, bus: { c: "bg-amber-50 text-amber-600", I: BusIcon } }[b.type || "flight"];
                  const I = meta.I;
                  return (
                    <tr key={b.booking_id || b.pnr} data-testid={`report-row-${b.pnr}`} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/60">
                      <td className="py-2.5 pr-3 font-extrabold text-[#1B1F5B]">{b.pnr}</td>
                      <td className="py-2.5 pr-3"><span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold capitalize ${meta.c}`}><I className="h-3 w-3" />{b.type || "flight"}</span></td>
                      <td className="py-2.5 pr-3 font-semibold text-slate-600">{b.route}</td>
                      <td className="py-2.5 pr-3 text-slate-500">{b.date}</td>
                      <td className="py-2.5 pr-3 text-slate-500">{b.pax}</td>
                      <td className="py-2.5 pr-3 text-right font-extrabold text-slate-800">{formatINR(b.total)}</td>
                      <td className="py-2.5 pr-3 text-right font-bold text-[#0E7C6B]">{b.commission ? formatINR(b.commission) : "—"}</td>
                      <td className="py-2.5 pr-3 text-right"><span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${b.status === "Confirmed" ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600"}`}>{b.status}</span></td>
                      <td className="py-2.5 text-right">
                        <button data-testid={`invoice-btn-${b.pnr}`} onClick={() => downloadPdf(b)} disabled={pdfBusy === b.booking_id}
                          className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2.5 py-1.5 text-[11px] font-bold text-[#1B1F5B] hover:border-[#0E7C6B] hover:text-[#0E7C6B] disabled:opacity-50">
                          {pdfBusy === b.booking_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />} PDF
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

// ---------------- ADMIN REPORTS (agent-wise leaderboard) ----------------
const AdminReportsPanel = () => {
  const [data, setData] = useState({ leaderboard: [], monthly: [], totals: {} });
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    apiAdminAgentReports().then(setData).catch(() => {}).finally(() => setLoading(false));
  }, []);
  const monthName = (m) => {
    const [y, mm] = (m || "").split("-");
    return mm ? new Date(y, mm - 1).toLocaleString("en", { month: "short" }) + " " + y.slice(2) : m;
  };
  const chartData = (data.monthly || []).map((m) => ({ ...m, label: monthName(m.month) }));
  const t = data.totals || {};
  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-[#0E7C6B]" /></div>;
  return (
    <div className="space-y-4" data-testid="admin-reports-panel">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {[
          { label: "Total Agents", value: t.agents ?? 0 },
          { label: "Total Bookings", value: t.bookings ?? 0 },
          { label: "Total Sales", value: formatINR(t.sales || 0) },
          { label: "Total Commission", value: formatINR(t.commission || 0) },
        ].map((k) => (
          <Card key={k.label}>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{k.label}</p>
            <p className="mt-1 text-xl font-extrabold text-slate-800">{k.value}</p>
          </Card>
        ))}
      </div>

      <Card>
        <div className="mb-2 flex items-center gap-2"><TrendingUp className="h-4 w-4 text-[#0E7C6B]" /><h3 className="text-base font-extrabold text-slate-800">Overall Monthly Sales</h3></div>
        {chartData.length === 0 ? <p className="py-10 text-center text-sm text-slate-400">No sales data yet.</p> : (
          <div className="h-64 w-full" data-testid="admin-reports-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} tickFormatter={(v) => v >= 1000 ? `${v / 1000}k` : v} />
                <Tooltip cursor={{ fill: "rgba(27,31,91,0.06)" }} formatter={(v) => [formatINR(v), "Sales"]} contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />
                <Bar dataKey="sales" radius={[6, 6, 0, 0]} maxBarSize={44}>{chartData.map((_, i) => <Cell key={i} fill="#1B1F5B" />)}</Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      <Card>
        <div className="mb-3 flex items-center gap-2"><Trophy className="h-4 w-4 text-[#F5B32B]" /><h3 className="text-base font-extrabold text-slate-800">Agent Sales Leaderboard</h3></div>
        {data.leaderboard.length === 0 ? <p className="py-10 text-center text-sm text-slate-400">No agents yet.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[680px] text-sm" data-testid="admin-leaderboard-table">
              <thead>
                <tr className="border-b border-slate-100 text-left text-[10px] font-bold uppercase tracking-wide text-slate-400">
                  <th className="pb-2 pr-3">#</th><th className="pb-2 pr-3">Agent</th><th className="pb-2 pr-3">Status</th>
                  <th className="pb-2 pr-3 text-right">Bookings</th><th className="pb-2 pr-3 text-right">Sales</th>
                  <th className="pb-2 pr-3 text-right">Commission</th><th className="pb-2 text-right">Wallet</th>
                </tr>
              </thead>
              <tbody>
                {data.leaderboard.map((a, i) => (
                  <tr key={a.agentId} data-testid={`leaderboard-row-${i}`} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/60">
                    <td className="py-2.5 pr-3">{i < 3 ? <span className={`flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-extrabold text-white ${["bg-[#F5B32B]", "bg-slate-400", "bg-amber-700"][i]}`}>{i + 1}</span> : <span className="pl-2 font-bold text-slate-400">{i + 1}</span>}</td>
                    <td className="py-2.5 pr-3"><p className="font-extrabold text-slate-700">{a.agency || a.name}</p><p className="text-[11px] text-slate-400">{a.email}</p></td>
                    <td className="py-2.5 pr-3"><span className={`rounded-full px-2 py-0.5 text-[10px] font-bold capitalize ${a.status === "active" ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600"}`}>{a.status}</span></td>
                    <td className="py-2.5 pr-3 text-right font-semibold text-slate-600">{a.bookings}</td>
                    <td className="py-2.5 pr-3 text-right font-extrabold text-slate-800">{formatINR(a.sales)}</td>
                    <td className="py-2.5 pr-3 text-right font-bold text-[#0E7C6B]">{formatINR(a.commission)}</td>
                    <td className="py-2.5 text-right font-semibold text-slate-500">{formatINR(a.wallet)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

// ---------------- ADMIN: HOLIDAY PACKAGES CRUD ----------------
const BLANK_HOLIDAY = {
  title: "", destination: "", country: "India", category: "Domestic", nights: 3, days: 4,
  price: 0, oldPrice: 0, image: "", rating: 4.5, summary: "",
  highlights: [], inclusions: ["Return Flights", "Hotel Stay", "Airport Transfers", "Daily Breakfast", "Sightseeing"],
  itinerary: [], themes: [], featured: false, active: true,
};
const HOLIDAY_CATS = ["Domestic", "International", "Honeymoon", "Family", "Adventure", "Weekend"];

const HolidaysAdminPanel = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {} draft
  const load = () => apiAdminListHolidays().then((d) => setList(d.holidays || [])).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const openNew = () => setEditing({ ...BLANK_HOLIDAY });
  const openEdit = (h) => setEditing({ ...h,
    highlights: (h.highlights || []).join("\n"), inclusions: (h.inclusions || []).join("\n") });

  const save = async () => {
    if (!editing.title || !editing.destination) return toast.error("Title & destination required");
    const payload = {
      ...editing,
      nights: Number(editing.nights) || 0, days: Number(editing.days) || 0,
      price: Number(editing.price) || 0, oldPrice: Number(editing.oldPrice) || 0,
      rating: Number(editing.rating) || 4.5,
      highlights: String(editing.highlights || "").split("\n").map((s) => s.trim()).filter(Boolean),
      inclusions: String(editing.inclusions || "").split("\n").map((s) => s.trim()).filter(Boolean),
      itinerary: Array.isArray(editing.itinerary) ? editing.itinerary : [],
    };
    // auto-build itinerary from highlights if none
    if (!payload.itinerary.length && payload.highlights.length) {
      payload.itinerary = payload.highlights.map((h, i) => ({ day: i + 1, title: h, desc: `${h} with transfers & assistance.` }));
    }
    try {
      if (editing.package_id) await apiAdminUpdateHoliday(editing.package_id, payload);
      else await apiAdminCreateHoliday(payload);
      toast.success(editing.package_id ? "Package updated" : "Package created");
      setEditing(null); load();
    } catch { toast.error("Could not save package"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this package?")) return;
    await apiAdminDeleteHoliday(id); toast.success("Deleted"); load();
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-[#0E7C6B]" /></div>;

  return (
    <div className="space-y-4" data-testid="admin-holidays-panel">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-extrabold text-slate-800">Holiday Packages</h3>
          <p className="text-xs text-slate-400">Create & manage packages shown on website, B2C & B2B</p>
        </div>
        <button data-testid="holiday-new-btn" onClick={openNew} className="flex items-center gap-1.5 rounded-xl bg-[#0E7C6B] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#0A5348]"><Plus className="h-4 w-4" /> New Package</button>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {list.length === 0 && <p className="py-10 text-center text-sm text-slate-400">No packages yet. Create your first one.</p>}
        {list.map((h) => (
          <div key={h.package_id} data-testid={`admin-holiday-${h.package_id}`} className="overflow-hidden rounded-xl border border-slate-100 bg-white shadow-sm">
            <div className="relative h-28"><img src={h.image} alt="" className="h-full w-full object-cover" />
              {!h.active && <span className="absolute left-2 top-2 rounded bg-slate-800/80 px-2 py-0.5 text-[10px] font-bold text-white">Hidden</span>}
              {h.featured && <span className="absolute right-2 top-2 rounded bg-[#F5B32B] px-2 py-0.5 text-[10px] font-extrabold text-[#0A0F2E]">Featured</span>}
            </div>
            <div className="p-3">
              <p className="text-sm font-extrabold text-slate-800">{h.title}</p>
              <p className="text-[11px] text-slate-400">{h.destination} · {h.nights}N/{h.days}D · {formatINR(h.price)}</p>
              <div className="mt-2 flex gap-2">
                <button data-testid={`holiday-edit-${h.package_id}`} onClick={() => openEdit(h)} className="flex flex-1 items-center justify-center gap-1 rounded-lg border border-slate-200 py-1.5 text-[11px] font-bold text-slate-600 hover:border-[#0E7C6B]"><Pencil className="h-3 w-3" /> Edit</button>
                <button data-testid={`holiday-del-${h.package_id}`} onClick={() => del(h.package_id)} className="flex items-center justify-center gap-1 rounded-lg border border-red-200 px-3 py-1.5 text-[11px] font-bold text-red-500 hover:bg-red-50"><Trash2 className="h-3 w-3" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4" data-testid="holiday-editor">
          <div className="my-4 w-full max-w-2xl rounded-2xl bg-white p-6 shadow-2xl">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-extrabold text-slate-800">{editing.package_id ? "Edit Package" : "New Package"}</h3>
              <button onClick={() => setEditing(null)} className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100"><X className="h-5 w-5" /></button>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <input data-testid="he-title" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} placeholder="Package title" className="sm:col-span-2 rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-[#0E7C6B]" />
              <input data-testid="he-destination" value={editing.destination} onChange={(e) => setEditing({ ...editing, destination: e.target.value })} placeholder="Destination (e.g. Goa)" className="rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-[#0E7C6B]" />
              <input value={editing.country} onChange={(e) => setEditing({ ...editing, country: e.target.value })} placeholder="Country" className="rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-[#0E7C6B]" />
              <select value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className="rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-[#0E7C6B]">
                {HOLIDAY_CATS.map((c) => <option key={c}>{c}</option>)}
              </select>
              <input value={editing.rating} type="number" step="0.1" onChange={(e) => setEditing({ ...editing, rating: e.target.value })} placeholder="Rating" className="rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-[#0E7C6B]" />
              <input value={editing.nights} type="number" onChange={(e) => setEditing({ ...editing, nights: e.target.value })} placeholder="Nights" className="rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-[#0E7C6B]" />
              <input value={editing.days} type="number" onChange={(e) => setEditing({ ...editing, days: e.target.value })} placeholder="Days" className="rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-[#0E7C6B]" />
              <input data-testid="he-price" value={editing.price} type="number" onChange={(e) => setEditing({ ...editing, price: e.target.value })} placeholder="Price / person" className="rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-[#0E7C6B]" />
              <input value={editing.oldPrice} type="number" onChange={(e) => setEditing({ ...editing, oldPrice: e.target.value })} placeholder="Old price (strikethrough)" className="rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-[#0E7C6B]" />
              <input data-testid="he-image" value={editing.image} onChange={(e) => setEditing({ ...editing, image: e.target.value })} placeholder="Image URL" className="sm:col-span-2 rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-[#0E7C6B]" />
              <textarea value={editing.summary} onChange={(e) => setEditing({ ...editing, summary: e.target.value })} rows={2} placeholder="Short summary" className="sm:col-span-2 rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-[#0E7C6B]" />
              <textarea value={editing.highlights} onChange={(e) => setEditing({ ...editing, highlights: e.target.value })} rows={3} placeholder="Highlights (one per line)" className="rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-[#0E7C6B]" />
              <textarea value={editing.inclusions} onChange={(e) => setEditing({ ...editing, inclusions: e.target.value })} rows={3} placeholder="Inclusions (one per line)" className="rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-[#0E7C6B]" />
              <label className="flex items-center gap-2 text-sm font-semibold text-slate-600"><input type="checkbox" checked={editing.featured} onChange={(e) => setEditing({ ...editing, featured: e.target.checked })} className="h-4 w-4 accent-[#0E7C6B]" /> Featured (Bestseller)</label>
              <label className="flex items-center gap-2 text-sm font-semibold text-slate-600"><input type="checkbox" checked={editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} className="h-4 w-4 accent-[#0E7C6B]" /> Active (visible)</label>
            </div>
            <div className="mt-4 flex gap-3">
              <button onClick={() => setEditing(null)} className="flex-1 rounded-xl border border-slate-200 py-3 text-sm font-bold text-slate-600">Cancel</button>
              <button data-testid="holiday-save-btn" onClick={save} className="flex-[2] rounded-xl bg-[#0E7C6B] py-3 text-sm font-extrabold text-white hover:bg-[#0A5348]">{editing.package_id ? "Update Package" : "Create Package"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const HolidayEnquiriesPanel = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { apiAdminHolidayEnquiries().then((d) => setList(d.enquiries || [])).catch(() => {}).finally(() => setLoading(false)); }, []);
  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-[#0E7C6B]" /></div>;
  return (
    <Card>
      <h3 className="text-base font-extrabold text-slate-800">Holiday Enquiries</h3>
      <div className="mt-3 space-y-2" data-testid="holiday-enquiries-list">
        {list.length === 0 && <p className="py-8 text-center text-sm text-slate-400">No enquiries yet.</p>}
        {list.map((e) => (
          <div key={e.enquiry_id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-slate-100 px-4 py-3 text-sm">
            <div>
              <p className="font-bold text-slate-700">{e.name} <span className="font-normal text-slate-400">· {e.pax} pax · {e.travelDate || "flexible"}</span></p>
              <p className="text-xs text-slate-400"><Mail className="mr-1 inline h-3 w-3" />{e.email} · {e.phone}</p>
              {e.message && <p className="mt-0.5 text-xs italic text-slate-500">"{e.message}"</p>}
            </div>
            <span className="rounded-full bg-amber-50 px-2.5 py-1 text-[11px] font-bold text-amber-600">{e.status}</span>
          </div>
        ))}
      </div>
    </Card>
  );
};

// ---------------- QUERIES HUB (all website enquiries) ----------------
const ENQ_TYPES = [
  { id: "all", label: "All" },
  { id: "visa", label: "Visa" },
  { id: "group", label: "Group Booking" },
  { id: "cab", label: "Cab" },
  { id: "train", label: "Train" },
  { id: "webcheckin", label: "Web Check-in" },
  { id: "contact", label: "Contact" },
  { id: "hotel", label: "Hotel" },
  { id: "general", label: "Other" },
];
const ENQ_STATUS = ["new", "contacted", "closed"];
const statusTint = (s) =>
  s === "closed" ? "bg-slate-100 text-slate-500" : s === "contacted" ? "bg-sky-50 text-sky-600" : "bg-amber-50 text-amber-600";

const QueriesHubPanel = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [type, setType] = useState("all");
  const [q, setQ] = useState("");
  const load = () => apiListEnquiries().then((d) => setList(d.enquiries || [])).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const setStatus = async (e, s) => {
    try { await apiEnquiryStatus(e.enquiry_id, s); toast.success(`Marked ${s}`); load(); }
    catch { toast.error("Could not update status"); }
  };

  const counts = ENQ_TYPES.reduce((a, t) => {
    a[t.id] = t.id === "all" ? list.length : list.filter((e) => (e.type || "general") === t.id).length;
    return a;
  }, {});
  const filtered = list.filter((e) => {
    const matchType = type === "all" || (e.type || "general") === type;
    const s = q.trim().toLowerCase();
    const matchQ = !s || [e.name, e.phone, e.email].some((v) => (v || "").toLowerCase().includes(s));
    return matchType && matchQ;
  });

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-[#0E7C6B]" /></div>;
  return (
    <div data-testid="queries-hub-panel">
      <div className="mb-3 flex flex-wrap gap-2">
        {ENQ_TYPES.map((t) => (
          <button key={t.id} data-testid={`enq-type-${t.id}`} onClick={() => setType(t.id)}
            className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-bold transition-colors ${type === t.id ? "border-[#1B1F5B] bg-[#1B1F5B] text-white" : "border-slate-200 bg-white text-slate-500 hover:border-[#1B1F5B]/40"}`}>
            {t.label}<span className={`rounded-full px-1.5 text-[10px] ${type === t.id ? "bg-white/20" : "bg-slate-100 text-slate-500"}`}>{counts[t.id] || 0}</span>
          </button>
        ))}
      </div>
      <div className="mb-3 flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2">
        <Search className="h-4 w-4 text-slate-400" />
        <input data-testid="enq-search" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name, phone or email" className="w-full text-sm outline-none" />
      </div>
      <div className="space-y-2" data-testid="queries-list">
        {filtered.length === 0 && <p className="rounded-2xl border border-dashed border-slate-200 bg-white py-12 text-center text-sm text-slate-400">No queries found.</p>}
        {filtered.map((e) => (
          <div key={e.enquiry_id} data-testid={`enq-row-${e.enquiry_id}`} className="rounded-xl border border-slate-100 bg-white p-4 shadow-sm">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <div className="flex items-center gap-2">
                  <span className="rounded-full bg-[#0E7C6B]/10 px-2 py-0.5 text-[10px] font-bold uppercase text-[#0E7C6B]">{e.type || "general"}</span>
                  <p className="font-bold text-slate-800">{e.name}</p>
                </div>
                <p className="mt-1 text-xs text-slate-500"><Phone className="mr-1 inline h-3 w-3" />{e.phone}{e.email ? <> · <Mail className="mr-1 inline h-3 w-3" />{e.email}</> : null}</p>
                {e.details && Object.keys(e.details).length > 0 && (
                  <p className="mt-1 text-xs italic text-slate-500">{Object.entries(e.details).map(([k, v]) => `${k}: ${v}`).join(" · ")}</p>
                )}
                <p className="mt-1 text-[11px] text-slate-400">{(e.created_at || "").slice(0, 16).replace("T", " ")}</p>
              </div>
              <div className="flex flex-col items-end gap-1.5">
                <span className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${statusTint(e.status)}`}>{e.status}</span>
                <div className="flex gap-1">
                  {ENQ_STATUS.filter((s) => s !== e.status).map((s) => (
                    <button key={s} data-testid={`enq-set-${s}-${e.enquiry_id}`} onClick={() => setStatus(e, s)}
                      className="rounded-lg border border-slate-200 px-2 py-1 text-[10px] font-bold text-slate-500 hover:border-[#1B1F5B] hover:text-[#1B1F5B]">
                      Mark {s}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ---------------- ALL BOOKINGS MANAGER (admin) ----------------
const bookingType = (b) => b.type || (b.flight ? "flight" : b.hotel ? "hotel" : "flight");
const BK_TABS = [
  { id: "all", label: "All", icon: ClipboardList },
  { id: "flight", label: "Flights", icon: Plane },
  { id: "hotel", label: "Hotels", icon: BedDouble },
  { id: "bus", label: "Bus", icon: BusIcon },
];

const AllBookingsAdminPanel = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  useEffect(() => { apiListBookings().then((d) => setList(d.bookings || [])).catch(() => {}).finally(() => setLoading(false)); }, []);

  const filtered = list.filter((b) => {
    const t = bookingType(b);
    if (tab !== "all" && t !== tab) return false;
    if (status !== "all" && (b.status || "").toLowerCase() !== status) return false;
    const s = q.trim().toLowerCase();
    if (s && ![b.pnr, b.route, b.user_email, b.passenger?.name].some((v) => (v || "").toLowerCase().includes(s))) return false;
    const d = (b.created_at || "").slice(0, 10);
    if (from && d && d < from) return false;
    if (to && d && d > to) return false;
    return true;
  });
  const totalRevenue = filtered.reduce((a, b) => a + (b.total || 0), 0);
  const counts = BK_TABS.reduce((a, t) => {
    a[t.id] = t.id === "all" ? list.length : list.filter((b) => bookingType(b) === t.id).length;
    return a;
  }, {});

  const exportCsv = () => {
    const rows = [["PNR", "Type", "Route", "Date", "Pax", "Amount", "Status", "Booked By", "Created"]];
    filtered.forEach((b) => rows.push([b.pnr, bookingType(b), b.route, b.date, b.pax, b.total, b.status, b.user_email, (b.created_at || "").slice(0, 16)]));
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = `bookings_${tab}.csv`; a.click(); URL.revokeObjectURL(url);
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-[#0E7C6B]" /></div>;
  return (
    <div data-testid="all-bookings-panel">
      <div className="mb-3 flex flex-wrap gap-2">
        {BK_TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.id} data-testid={`bk-tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 rounded-xl border px-3.5 py-2 text-xs font-bold transition-colors ${tab === t.id ? "border-[#1B1F5B] bg-[#1B1F5B] text-white" : "border-slate-200 bg-white text-slate-500 hover:border-[#1B1F5B]/40"}`}>
              <Icon className="h-3.5 w-3.5" /> {t.label}<span className={`rounded-full px-1.5 text-[10px] ${tab === t.id ? "bg-white/20" : "bg-slate-100 text-slate-500"}`}>{counts[t.id] || 0}</span>
            </button>
          );
        })}
      </div>
      <div className="mb-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
        <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 lg:col-span-2">
          <Search className="h-4 w-4 text-slate-400" />
          <input data-testid="bk-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search PNR / route / email" className="w-full text-sm outline-none" />
        </div>
        <select data-testid="bk-status" value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none">
          <option value="all">All Status</option>
          <option value="confirmed">Confirmed</option>
          <option value="cancelled">Cancelled</option>
          <option value="pending">Pending</option>
        </select>
        <input data-testid="bk-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none" />
        <input data-testid="bk-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none" />
      </div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-slate-500"><span className="font-extrabold text-slate-800">{filtered.length}</span> bookings · <span className="font-extrabold text-[#1B1F5B]">{formatINR(totalRevenue)}</span> revenue</p>
        <button data-testid="bk-export" onClick={exportCsv} className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold text-slate-600 hover:border-[#1B1F5B]"><Download className="h-3.5 w-3.5" /> Export CSV</button>
      </div>
      <div className="overflow-x-auto rounded-2xl border border-slate-100 bg-white shadow-sm" data-testid="bk-table">
        <table className="w-full min-w-[760px] text-sm">
          <thead>
            <tr className="border-b bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-400">
              <th className="px-4 py-3">PNR</th><th className="px-4 py-3">Type</th><th className="px-4 py-3">Route / Hotel</th>
              <th className="px-4 py-3">Date</th><th className="px-4 py-3">Pax</th><th className="px-4 py-3">Amount</th>
              <th className="px-4 py-3">Status</th><th className="px-4 py-3">Booked By</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400">No bookings match your filters.</td></tr>}
            {filtered.map((b) => {
              const t = bookingType(b);
              const Icon = t === "hotel" ? BedDouble : t === "bus" ? BusIcon : Plane;
              return (
                <tr key={b.pnr} data-testid={`bk-row-${b.pnr}`} className="border-b last:border-0 hover:bg-slate-50/60">
                  <td className="px-4 py-3 font-bold text-[#1B1F5B]">{b.pnr}</td>
                  <td className="px-4 py-3"><span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-bold capitalize text-slate-600"><Icon className="h-3 w-3" />{t}</span></td>
                  <td className="px-4 py-3 text-slate-600">{b.route}</td>
                  <td className="px-4 py-3 text-slate-500">{b.date}</td>
                  <td className="px-4 py-3 text-slate-500">{b.pax}</td>
                  <td className="px-4 py-3 font-extrabold text-slate-800">{formatINR(b.total)}</td>
                  <td className="px-4 py-3"><span className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${(b.status || "").toLowerCase() === "cancelled" ? "bg-red-50 text-red-500" : "bg-emerald-50 text-emerald-600"}`}>{b.status}</span></td>
                  <td className="px-4 py-3 text-xs text-slate-400">{b.user_email}<br /><span className="capitalize">{b.user_role}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};


// ---------------- WEBSITE SETTINGS: BANNERS ----------------
const emptyBanner = { title: "", subtitle: "", image: "", link: "", cta: "Explore", order: 0, active: true };
const WebsiteBannersPanel = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyBanner);
  const [editId, setEditId] = useState(null);
  const load = () => apiAdminListBanners().then((d) => setList(d.banners || [])).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.title || !form.image) { toast.error("Title and image URL required"); return; }
    try {
      if (editId) await apiAdminUpdateBanner(editId, form);
      else await apiAdminCreateBanner(form);
      toast.success(editId ? "Banner updated" : "Banner added");
      setForm(emptyBanner); setEditId(null); load();
    } catch { toast.error("Could not save banner"); }
  };
  const edit = (b) => { setEditId(b.banner_id); setForm({ title: b.title, subtitle: b.subtitle || "", image: b.image, link: b.link || "", cta: b.cta || "Explore", order: b.order || 0, active: b.active }); };
  const del = async (id) => { await apiAdminDeleteBanner(id); toast.success("Deleted"); load(); };
  const toggle = async (b) => { await apiAdminUpdateBanner(b.banner_id, { ...b, active: !b.active }); load(); };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-[#0E7C6B]" /></div>;
  return (
    <div data-testid="website-banners-panel" className="grid gap-4 lg:grid-cols-[1fr,340px]">
      <div className="space-y-3">
        <h3 className="text-base font-extrabold text-slate-800">Homepage Banners</h3>
        {list.length === 0 && <p className="rounded-2xl border border-dashed border-slate-200 bg-white py-10 text-center text-sm text-slate-400">No banners yet. Add one on the right.</p>}
        {list.map((b) => (
          <div key={b.banner_id} data-testid={`banner-row-${b.banner_id}`} className="flex items-center gap-3 rounded-xl border border-slate-100 bg-white p-3 shadow-sm">
            <img src={b.image} alt="" className="h-16 w-28 shrink-0 rounded-lg object-cover" />
            <div className="min-w-0 flex-1">
              <p className="truncate font-bold text-slate-800">{b.title}</p>
              <p className="truncate text-xs text-slate-400">{b.subtitle}</p>
              <p className="text-[11px] text-slate-400">Order {b.order} · {b.active ? "Active" : "Hidden"}</p>
            </div>
            <button data-testid={`banner-toggle-${b.banner_id}`} onClick={() => toggle(b)} title="Toggle active">
              {b.active ? <ToggleRight className="h-6 w-6 text-emerald-500" /> : <ToggleLeft className="h-6 w-6 text-slate-300" />}
            </button>
            <button data-testid={`banner-edit-${b.banner_id}`} onClick={() => edit(b)} className="rounded-lg border border-slate-200 p-2 text-slate-500 hover:border-[#1B1F5B]"><Pencil className="h-4 w-4" /></button>
            <button data-testid={`banner-del-${b.banner_id}`} onClick={() => del(b.banner_id)} className="rounded-lg border border-red-200 p-2 text-red-500 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
      <Card className="h-fit">
        <h4 className="text-sm font-extrabold text-slate-800">{editId ? "Edit Banner" : "Add Banner"}</h4>
        <div className="mt-3 space-y-2">
          <Field data-testid="banner-title" placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          <Field data-testid="banner-subtitle" placeholder="Subtitle" value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} />
          <Field data-testid="banner-image" placeholder="Image URL" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} />
          <Field data-testid="banner-link" placeholder="Link (e.g. /package)" value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} />
          <div className="flex gap-2">
            <Field data-testid="banner-cta" placeholder="Button text" value={form.cta} onChange={(e) => setForm({ ...form, cta: e.target.value })} />
            <Field data-testid="banner-order" type="number" placeholder="Order" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })} />
          </div>
          <label className="flex items-center gap-2 text-xs font-bold text-slate-600">
            <input type="checkbox" data-testid="banner-active" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Active
          </label>
          <div className="flex gap-2">
            <Btn data-testid="banner-save" onClick={save}>{editId ? "Update" : "Add Banner"}</Btn>
            {editId && <button onClick={() => { setForm(emptyBanner); setEditId(null); }} className="rounded-lg border border-slate-200 px-4 text-sm font-bold text-slate-500">Cancel</button>}
          </div>
        </div>
      </Card>
    </div>
  );
};

// ---------------- SEO MANAGEMENT ----------------
const SEO_PAGES = ["home", "flights", "hotels", "holidays", "bus", "visa"];
const SeoPanel = () => {
  const [seo, setSeo] = useState({});
  const [page, setPage] = useState("home");
  const [form, setForm] = useState({ title: "", description: "", keywords: "" });
  useEffect(() => { apiAdminListSeo().then((d) => {
    const map = {}; (d.seo || []).forEach((s) => { map[s.page] = s; }); setSeo(map);
  }).catch(() => {}); }, []);
  useEffect(() => { const s = seo[page] || {}; setForm({ title: s.title || "", description: s.description || "", keywords: s.keywords || "" }); }, [page, seo]);

  const save = async () => {
    try { await apiAdminSetSeo({ page, ...form }); setSeo({ ...seo, [page]: { page, ...form } }); toast.success("SEO saved for " + page); }
    catch { toast.error("Could not save SEO"); }
  };
  return (
    <Card data-testid="seo-panel">
      <h3 className="text-base font-extrabold text-slate-800">SEO Management</h3>
      <p className="mb-3 text-xs text-slate-400">Set meta title, description & keywords per page.</p>
      <div className="mb-3 flex flex-wrap gap-2">
        {SEO_PAGES.map((p) => (
          <button key={p} data-testid={`seo-page-${p}`} onClick={() => setPage(p)}
            className={`rounded-full border px-3 py-1.5 text-xs font-bold capitalize ${page === p ? "border-[#1B1F5B] bg-[#1B1F5B] text-white" : "border-slate-200 bg-white text-slate-500"}`}>{p}</button>
        ))}
      </div>
      <div className="space-y-2">
        <Field data-testid="seo-title" placeholder="Meta title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        <textarea data-testid="seo-desc" placeholder="Meta description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
          className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-[#1B1F5B]" rows={3} />
        <Field data-testid="seo-keywords" placeholder="Keywords (comma separated)" value={form.keywords} onChange={(e) => setForm({ ...form, keywords: e.target.value })} />
        <Btn data-testid="seo-save" onClick={save}>Save SEO</Btn>
      </div>
    </Card>
  );
};

// ---------------- SUBSCRIBERS ----------------
const SubscribersPanel = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { apiAdminSubscribers().then((d) => setList(d.subscribers || [])).catch(() => {}).finally(() => setLoading(false)); }, []);
  const exportCsv = () => {
    const csv = "email,subscribed_on\n" + list.map((s) => `${s.email},${(s.created_at || "").slice(0, 10)}`).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = "subscribers.csv"; a.click(); URL.revokeObjectURL(url);
  };
  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-[#0E7C6B]" /></div>;
  return (
    <Card data-testid="subscribers-panel">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-extrabold text-slate-800">Newsletter Subscribers <span className="text-sm font-normal text-slate-400">({list.length})</span></h3>
        <button data-testid="subs-export" onClick={exportCsv} className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-bold text-slate-600 hover:border-[#1B1F5B]"><Download className="h-3.5 w-3.5" /> Export</button>
      </div>
      <div className="mt-3 divide-y divide-slate-100" data-testid="subs-list">
        {list.length === 0 && <p className="py-8 text-center text-sm text-slate-400">No subscribers yet.</p>}
        {list.map((s) => (
          <div key={s.email} className="flex items-center justify-between py-2.5 text-sm">
            <span className="flex items-center gap-2 text-slate-700"><AtSign className="h-4 w-4 text-[#0E7C6B]" />{s.email}</span>
            <span className="text-xs text-slate-400">{(s.created_at || "").slice(0, 10)}</span>
          </div>
        ))}
      </div>
    </Card>
  );
};

// ---------------- FINANCE: CREDIT/DEBIT NOTES + REFUNDS ----------------
const FinancePanel = () => {
  const [notes, setNotes] = useState([]);
  const [refunds, setRefunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ kind: "credit", party: "", amount: 0, reason: "", reference: "" });
  const load = () => Promise.all([apiAdminListNotes(), apiListRefunds()])
    .then(([n, r]) => { setNotes(n.notes || []); setRefunds(r.refunds || []); })
    .catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const addNote = async () => {
    if (!form.party || !form.amount) { toast.error("Party and amount required"); return; }
    try { await apiAdminCreateNote(form); toast.success("Note created"); setForm({ ...form, party: "", amount: 0, reason: "", reference: "" }); load(); }
    catch { toast.error("Could not create note"); }
  };
  const delNote = async (id) => { await apiAdminDeleteNote(id); load(); };
  const setRefund = async (id, s) => { try { await apiRefundStatus(id, s); toast.success(`Refund ${s}`); load(); } catch { toast.error("Could not update"); } };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-[#0E7C6B]" /></div>;
  return (
    <div data-testid="finance-panel" className="space-y-5">
      <Card>
        <h3 className="text-base font-extrabold text-slate-800">Credit / Debit Notes</h3>
        <div className="mt-3 grid gap-2 sm:grid-cols-6">
          <select data-testid="note-kind" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} className="rounded-lg border border-slate-200 px-2 py-2.5 text-sm">
            <option value="credit">Credit Note</option><option value="debit">Debit Note</option>
          </select>
          <Field data-testid="note-party" className="sm:col-span-2" placeholder="Party (agent/customer)" value={form.party} onChange={(e) => setForm({ ...form, party: e.target.value })} />
          <Field data-testid="note-amount" type="number" placeholder="Amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: parseInt(e.target.value) || 0 })} />
          <Field data-testid="note-reason" placeholder="Reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
          <Btn data-testid="note-add" onClick={addNote}>Issue</Btn>
        </div>
        <div className="mt-4 space-y-2" data-testid="notes-list">
          {notes.length === 0 && <p className="py-6 text-center text-sm text-slate-400">No notes issued yet.</p>}
          {notes.map((n) => (
            <div key={n.note_id} className="flex items-center justify-between rounded-xl border border-slate-100 px-4 py-2.5 text-sm">
              <span className="flex items-center gap-2">
                {n.kind === "credit" ? <FilePlus className="h-4 w-4 text-emerald-500" /> : <FileMinus className="h-4 w-4 text-red-500" />}
                <span className="font-bold text-slate-700">{n.number}</span>
                <span className="text-slate-500">· {n.party}</span>
                {n.reason && <span className="text-xs italic text-slate-400">({n.reason})</span>}
              </span>
              <span className="flex items-center gap-3">
                <span className={`font-extrabold ${n.kind === "credit" ? "text-emerald-600" : "text-red-500"}`}>{n.kind === "credit" ? "+" : "-"}{formatINR(n.amount)}</span>
                <button data-testid={`note-del-${n.note_id}`} onClick={() => delNote(n.note_id)} className="text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
              </span>
            </div>
          ))}
        </div>
      </Card>
      <Card>
        <h3 className="text-base font-extrabold text-slate-800">Refund Requests</h3>
        <div className="mt-3 space-y-2" data-testid="refunds-list">
          {refunds.length === 0 && <p className="py-6 text-center text-sm text-slate-400">No refund requests.</p>}
          {refunds.map((r) => (
            <div key={r.refund_id} data-testid={`refund-row-${r.refund_id}`} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-slate-100 px-4 py-3 text-sm">
              <div>
                <p className="font-bold text-slate-700"><Undo2 className="mr-1 inline h-4 w-4 text-amber-500" />{r.pnr} · {formatINR(r.amount)}</p>
                <p className="text-xs text-slate-400">{r.user_email} {r.reason ? `· ${r.reason}` : ""}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${r.status === "processed" ? "bg-emerald-50 text-emerald-600" : r.status === "rejected" ? "bg-red-50 text-red-500" : r.status === "approved" ? "bg-sky-50 text-sky-600" : "bg-amber-50 text-amber-600"}`}>{r.status}</span>
                {r.status !== "processed" && r.status !== "rejected" && (
                  <>
                    {r.status === "requested" && <button data-testid={`refund-approve-${r.refund_id}`} onClick={() => setRefund(r.refund_id, "approved")} className="rounded-lg bg-sky-500 px-2.5 py-1.5 text-[11px] font-bold text-white">Approve</button>}
                    <button data-testid={`refund-process-${r.refund_id}`} onClick={() => setRefund(r.refund_id, "processed")} className="rounded-lg bg-emerald-500 px-2.5 py-1.5 text-[11px] font-bold text-white">Process</button>
                    <button data-testid={`refund-reject-${r.refund_id}`} onClick={() => setRefund(r.refund_id, "rejected")} className="rounded-lg bg-red-500 px-2.5 py-1.5 text-[11px] font-bold text-white">Reject</button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};


// ---------------- MAIN ----------------

export const AGENT_TABS = [
  { id: "markup", label: "Markup", icon: Sliders },
  { id: "statement", label: "Statement", icon: ReceiptText },
  { id: "topup", label: "Add Money", icon: Wallet },
  { id: "reports", label: "Reports", icon: FileBarChart },
  { id: "service", label: "Service Request", icon: Headset },
  { id: "customers", label: "My Customers", icon: Users2 },
  { id: "quotes", label: "My Quotations", icon: FileText },
  { id: "amendments", label: "Cancel/Reschedule", icon: RefreshCcw },
];
export const ADMIN_TABS = [
  { id: "allbookings", label: "All Bookings", icon: ClipboardList },
  { id: "queries", label: "Queries Hub", icon: MessageSquare },
  { id: "deposits", label: "Top-up Approvals", icon: Wallet },
  { id: "walletloads", label: "Wallet Loads", icon: Wallet },
  { id: "rep-flight", label: "Flight Report", icon: FileBarChart },
  { id: "rep-hotel", label: "Hotel Report", icon: FileBarChart },
  { id: "rep-bus", label: "Bus Report", icon: FileBarChart },
  { id: "rep-holiday", label: "Holiday Report", icon: FileBarChart },
  { id: "rep-wallet", label: "Wallet Report", icon: FileBarChart },
  { id: "reports", label: "Agent Sales Summary", icon: FileBarChart },
  { id: "mk-overview", label: "B2C Markup", icon: Sliders },
  { id: "holidays", label: "Holidays", icon: Palmtree },
  { id: "enquiries", label: "Holiday Enquiries", icon: Mail },
  { id: "markup", label: "Markup Settings", icon: Sliders },
  { id: "offers", label: "Offers / Coupons", icon: BadgePercent },
  { id: "service", label: "Service Requests", icon: Headset },
  { id: "amendments", label: "Amendment Tracking", icon: RefreshCcw },
  { id: "finance", label: "Finance (Notes/Refunds)", icon: ReceiptText },
  { id: "banks", label: "Bank Details", icon: Landmark },
  { id: "website", label: "Website Banners", icon: ImageIcon },
  { id: "seo", label: "SEO Management", icon: Globe },
  { id: "subscribers", label: "Subscribers", icon: AtSign },
];

export const B2BModules = ({ role, tab: tabProp, setTab: setTabProp, hideTabs = false, mobileTabsOnly = false }) => {
  const tabs = role === "admin" ? ADMIN_TABS : AGENT_TABS;
  const [tabState, setTabState] = useState(tabs[0].id);
  const tab = tabProp ?? tabState;
  const setTab = setTabProp ?? setTabState;
  return (
    <div className="mt-6" data-testid="b2b-modules">
      {!hideTabs && (
        <div className={`mb-4 flex flex-wrap gap-2 ${mobileTabsOnly ? "lg:hidden" : ""}`}>
          {tabs.map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.id} data-testid={`b2b-tab-${t.id}`} onClick={() => setTab(t.id)}
                className={`flex items-center gap-1.5 rounded-xl border px-3.5 py-2 text-xs font-bold transition-colors ${tab === t.id ? "border-[#0E7C6B] bg-[#0E7C6B] text-white" : "border-slate-200 bg-white text-slate-500 hover:border-[#0E7C6B]/40"}`}>
                <Icon className="h-3.5 w-3.5" /> {t.label}
              </button>
            );
          })}
        </div>
      )}
      <div>
        {tab === "markup" && <MarkupPanel />}
        {tab === "statement" && <StatementPanel />}
        {tab === "topup" && <TopupPanel />}
        {tab === "service" && <ServicePanel admin={role === "admin"} />}
        {tab === "customers" && <TravellersPanel />}
        {tab === "quotes" && <SavedQuotes />}
        {tab === "amendments" && <AmendmentsPanel admin={role === "admin"} />}
        {tab === "reports" && (role === "admin" ? <AdminReportsPanel /> : <ReportsPanel />)}
        {tab === "holidays" && <HolidaysAdminPanel />}
        {tab === "enquiries" && <HolidayEnquiriesPanel />}
        {tab === "deposits" && <DepositApprovals />}
        {tab === "walletloads" && <WalletLoadsPanel />}
        {tab === "offers" && <OffersPanel />}
        {tab === "banks" && <BanksPanel />}
        {tab === "allbookings" && <AllBookingsAdminPanel />}
        {tab === "queries" && <QueriesHubPanel />}
        {tab === "website" && <WebsiteBannersPanel />}
        {tab === "seo" && <SeoPanel />}
        {tab === "subscribers" && <SubscribersPanel />}
        {tab === "finance" && <FinancePanel />}
        {tab.startsWith("rep-") && <ReportCenter key={tab} kind={tab.slice(4)} admin={role === "admin"} platform={role === "admin"} />}
        {tab === "mk-overview" && <MarkupOverview onOpen={(p, k) => setTab(`mk-${p}:${k}`)} />}
        {/^mk-(flight|hotel|bus)/.test(tab) && <GroupManager key={tab} product={tab.slice(3).split(":")[0]} kind={tab.split(":")[1]} />}
      </div>
    </div>
  );
};

export default B2BModules;
